import React from 'react';
import { useAccessControl } from '../../context/AccessControlContext';
import { useNavigate } from 'react-router-dom';
import { showErrorBanner } from './ErrorBannerManager';

/**
 * ProtectedAction - Click interceptor for access-controlled features
 * 
 * Instead of visually blocking UI, this component:
 * 1. Renders children normally (no visual changes)
 * 2. Intercepts clicks when user lacks access
 * 3. Shows notification banner with appropriate message
 * 4. PREVENTS the original onClick from firing when blocked
 */
const ProtectedAction = ({ 
  feature, 
  children, 
  customMessage = null,
  redirectOnBlock = true
}) => {
  const { can, category, actionNeeded } = useAccessControl();
  const navigate = useNavigate();
  
  const canPerform = can(feature);
  
  // If user has access, render normally
  if (canPerform) {
    return <>{children}</>;
  }
  
  // User cannot perform this action - intercept clicks and show notification
  const handleBlockedClick = (e) => {
    // CRITICAL: Stop all event propagation to prevent the underlying action
    e.preventDefault();
    e.stopPropagation();
    
    // Determine appropriate message and action based on category
    let title = 'Action Not Available';
    let message = customMessage;
    let redirectPath = null;
    let actionLabel = 'View Plans';
    
    if (!message) {
      switch (category) {
        case 'no_access':
          title = 'Subscription Required';
          message = 'Subscribe to a plan to access this feature. Start with a free 7-day trial!';
          redirectPath = '/settings/billing';
          actionLabel = 'View Plans';
          break;
        case 'active_no_credits':
          title = 'Credits Required';
          message = 'Add credits to your account to continue using this feature.';
          redirectPath = '/settings/usage';
          actionLabel = 'Add Credits';
          break;
        case 'trial_or_grace':
          // Special message for trial users trying to buy credits
          if (feature === 'add_credits') {
            title = 'Trial Period Active';
            message = 'Upgrade to a paid plan to purchase additional credits. Your trial includes free credits to get started.';
            redirectPath = '/settings/billing';
            actionLabel = 'View Plans';
          } else {
            title = 'Upgrade Required';
            message = 'This action requires an active subscription. Upgrade to continue.';
            redirectPath = '/settings/billing';
            actionLabel = 'Upgrade Plan';
          }
          break;
        default:
          message = actionNeeded?.message || 'This action is not available with your current plan.';
          redirectPath = actionNeeded?.redirect || '/settings/billing';
          actionLabel = 'Learn More';
      }
    }
    
    // Show notification banner
    showErrorBanner(
      { message, title },
      {
        type: 'error',
        position: 'top-right',
        autoHideDelay: 6000,
        action: redirectOnBlock && redirectPath ? {
          label: actionLabel,
          onClick: () => navigate(redirectPath)
        } : null
      }
    );
  };
  
  // Clone children and override their onClick to intercept
  const blockedChildren = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      // Override onClick to show notification instead
      return React.cloneElement(child, {
        onClick: handleBlockedClick,
        // Also override any onMouseDown/onPointerDown to be safe
        onMouseDown: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        onPointerDown: (e) => {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    }
    return child;
  });
  
  return <>{blockedChildren}</>;
};

export default ProtectedAction;

